// cesium.ts - Main Cesium implementation file

import {
  CameraEventType,
  Cartesian3,
  Cartographic,
  Cesium3DTileset,
  Math as CesiumMath,
  ClassificationType,
  ClippingPlane,
  ClippingPlaneCollection,
  ClippingPolygon,
  ClippingPolygonCollection,
  Color,
  ColorMaterialProperty,
  createOsmBuildingsAsync,
  createWorldImageryAsync,
  EllipsoidTerrainProvider,
  HeightReference,
  Ion,
  IonResource,
  IonWorldImageryStyle,
  JulianDate,
  OpenStreetMapImageryProvider,
  PolygonHierarchy,
  RequestScheduler,
  Resource,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Terrain,
  Viewer,
} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";

import {
  findAssetById as _findAssetById,
  loadAndZoomToAsset as _loadAndZoomToAsset,
  removeAssetById as _removeAssetById,
  zoomToAssetById as _zoomToAssetById,
  loadMultipleTilesets,
  setupZoomToTileset,
  tilesetRegistry,
} from "./cesium-assets";
import {
  flyToPosition as _flyToPosition,
  flyToView as _flyToView,
  getCurrentCameraPosition as _getCurrentCameraPosition,
  setCameraPosition as _setCameraPosition,
  updateCameraPosition as _updateCameraPosition,
  createCameraOptions,
  resetCameraState,
  setCameraReady,
  setupCameraEvents,
} from "./cesium-camera";
import {
  setBimHighlightingEnabled,
  setupBimHighlightingForExistingTileset,
  setupClickHandlers,
  setupGlobalEvents,
  setupHoverHandlers,
} from "./cesium-events";
import { getSimpleGeoidUndulation } from "./cesium-geoid";
import { initializeGeoidWithOffset } from "./cesium-geoid-init";
import {
  changeImageryProvider,
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "./cesium-imagery";
import { applyInitialStyles, applyStylesToAllTilesets } from "./cesium-styling";
import type {
  CesiumViewSettings,
  ImageryStyle,
  InitializeCesiumOptions,
  StyleCondition,
} from "./cesium-types";

// Set Cesium Ion access token

Ion.defaultAccessToken = import.meta.env.VITE_CESIUM_ACCESS_TOKEN;

// Configure Cesium resource handling and request scheduling for performance
(Resource as any).retryAttempts = 0; // No retries for faster feedback
(Resource as any).retryCallback = undefined;

// Simple request scheduling optimization for faster asset loading
if (
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
) {
  // Conservative mobile settings to prevent crashes
  RequestScheduler.maximumRequestsPerServer = 4; // Lowered for mobile stability
  RequestScheduler.maximumRequests = 8; // Low total for mobile
} else {
  // Desktop can handle more aggressive settings
  RequestScheduler.maximumRequestsPerServer = 8;
  RequestScheduler.maximumRequests = 32;
}

// Global viewer instance
let viewer: any = null;
let isMobileDevice = false;

// Track global tilesets for dynamic toggling
const osmTileset: any = null;
const googlePhotorealisticTileset: any = null;

// Polygon drawing state
const isDrawingPolygon = false;
const polygonPoints: any[] = [];
const activePolygonEntity: any = null;

// Utility functions
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

// Configure viewer options based on device type and Google Photorealistic usage
function createViewerOptions(
  isMobile: boolean,
  enablePerformanceMode = false,
): any {
  const baseOptions = {
    // Always use world terrain during construction to avoid init errors
    terrain: Terrain.fromWorldTerrain(),
    // Note: Will switch to ellipsoid and remove imagery after creation if Google is used
    navigationHelpButton: false,
    homeButton: false,
    geocoder: false,
    timeline: false,
    fullscreenButton: false,
    animation: false,
    sceneModePicker: false,
    baseLayerPicker: false,
    infoBox: false,
    targetFrameRate: isMobile ? 30 : 30, // Lowered frame rate for mobile stability
    scene3DOnly: true, // Performance: Render for 3D only
    // requestRenderMode: true, // Only render when needed
    // maximumRenderTimeChange: Number.POSITIVE_INFINITY,
    orderIndependentTranslucency: false, // Performance: Disable OIT for speed
    // useBrowserRecommendedResolution: !enablePerformanceMode, // Performance: Use CSS pixels on high-DPI devices
    useBrowserRecommendedResolution: false,
    // Aggressive performance optimizations
    shadows: false, // Disable shadows (expensive)
    terrainShadows: false,
    tileCacheSize: 50, // Limit tile cache size

    msaaSamples: enablePerformanceMode ? 1 : 4, // Reduce MSAA samples in performance mode

    contextOptions: {
      webgl: {
        alpha: false,
        antialias: !enablePerformanceMode, // Disable antialiasing for performance
        powerPreference: "low-power", // Prioritize performance over quality
        depth: true,
        stencil: false,
        premultipliedAlpha: true,
        preserveDrawingBuffer: false,
        failIfMajorPerformanceCaveat: false,
      },
    },
  };

  return baseOptions;
}

// Configure scene after viewer creation
function configureSceneAfterCreation(viewer: any) {
  // Set sky atmosphere and lighting

  viewer.scene.skyAtmosphere.show = true;
  viewer.scene.globe.enableLighting = false;
  viewer.scene.sun.show = true;
  viewer.scene.sun.glowFactor = 10;
  viewer.scene.moon.show = false;
  viewer.scene.globe.depthTestAgainstTerrain = false; // Enable depth testing to prevent z-fighting
  // viewer.scene.globe.maximumScreenSpaceError = 5;
  viewer.scene.globe.tileCacheSize = 1000; // default 100

  // Set background colors
  viewer.scene.backgroundColor = Color.fromCssColorString("#f9f8f7");
  viewer.scene.globe.baseColor =
    Color.fromCssColorString("#f9f8f7").withAlpha(0);

  viewer.scene.skyBox.show = false;

  viewer.scene.postProcessStages.fxaa.enabled = true;

  // Set appropriate screen space error based on device type - this is the correct place for this setting
  // viewer.scene.globe.maximumScreenSpaceError = isMobileDevice ? 16 : 16; // Reverted to safe value to prevent crashes

  // Apply aggressive LOD settings for mobile performance
  if (isMobileDevice) {
    // More aggressive LOD settings for mobile to improve performance
    viewer.scene.globe.skipLevelOfDetail = true;

    // High skip screen space error factor for aggressive skipping
    viewer.scene.globe.skipScreenSpaceErrorFactor = 16;

    // Skip multiple detail levels
    viewer.scene.globe.skipLevels = 2;

    // Conservative mobile optimizations for stability
    viewer.scene.globe.loadingDescendantLimit = 20; // Limit concurrent tile loads
    viewer.scene.globe.preloadSiblings = false; // Don't preload sibling tiles
    viewer.scene.globe.preloadAncestors = false; // Don't preload ancestor tiles

    // Apply lowered resolution scale for better performance
    viewer.resolutionScale = 0.75;
  }

  // Ensure proper render mode for consistent performance
  // viewer.scene.requestRenderMode = true;
  // viewer.scene.maximumRenderTimeChange = Number.POSITIVE_INFINITY;
}

// Function to set the scene's time
function setSceneTime(viewer: any) {
  // Set the time to 15:00 on April 1st German Time (CEST = UTC+2)
  const aprilFirst = new Date();
  aprilFirst.setUTCMonth(7, 1); // May is month 5 (0-indexed), day 1
  aprilFirst.setUTCHours(13, 30, 0, 0); // 13:00 UTC is 15:00 German Summer Time (CEST)
  viewer.clock.currentTime = JulianDate.fromDate(aprilFirst);
  viewer.scene.requestRender(); // Ensure the scene re-renders with the new time
}

// Setup camera controls
function setupCameraControls(viewer: any) {
  viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1.0;
  const controller = viewer.scene.screenSpaceCameraController;

  if (isMobileDevice) {
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  } else {
    controller.tiltEventTypes = [CameraEventType.RIGHT_DRAG];
    controller.rotateEventTypes = [CameraEventType.LEFT_DRAG];
    controller.zoomEventTypes = [CameraEventType.WHEEL, CameraEventType.PINCH];
    controller.translateEventTypes = [CameraEventType.MIDDLE_DRAG];
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
  }

  // Disable inertia for immediate response and better performance
  controller.inertiaSpin = 0.0;
  controller.inertiaTranslate = 0.0;
  controller.inertiaZoom = 0.0;
}

// Main initialization function
export async function initializeCesium(
  options: InitializeCesiumOptions = {},
): Promise<void> {
  const {
    containerId = "cesiumContainer",
    onObjectClick,
    assetIds = [],
    bimAssetStyles,
    assetMetadataMap,
    mapData,
    imageryStyle,
    onCameraMove,
    onViewCenterChange,
    onReady,
    initialCameraPosition,
    autoZoomToAssets,
    clippingRectangle,
    cacheBytes,
    maximumCacheOverflowBytes,
    tileCacheSize,
  } = options;

  try {
    isMobileDevice = isMobile();
    const hasInitialCameraPosition = !!initialCameraPosition;

    // Check if container has valid dimensions before initializing Cesium
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`);
    }

    const containerRect = container.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) {
      throw new Error(
        `Container has zero dimensions: ${containerRect.width}x${containerRect.height}`,
      );
    }

    console.log(
      `Initializing Cesium with container dimensions: ${containerRect.width}x${containerRect.height}`,
    );

    // Create and configure viewer
    const viewerOptions = createViewerOptions(
      isMobileDevice,
      shouldEnablePerformanceMode(),
    );
    viewer = new Viewer(containerId, viewerOptions);

    if (!viewer) {
      return;
    }

    // Store viewer globally for access from other modules
    (window as any).cesiumViewer = viewer;

    // Configure the scene with our settings
    configureSceneAfterCreation(viewer);

    viewer.scene.globe.tileCacheSize =
      tileCacheSize || (isMobileDevice ? 50 : 200);

    setSceneTime(viewer);

    // Don't remove imagery layers during initialization - let the toggle handle it
    // Setup camera controls
    setupCameraControls(viewer);

    // Setup zoom to tileset functionality
    setupZoomToTileset(viewer, hasInitialCameraPosition, autoZoomToAssets);

    // Setup event handlers
    setupCameraEvents(
      viewer,
      tilesetRegistry,
      onCameraMove,
      onViewCenterChange,
    );
    setupClickHandlers(viewer, onObjectClick);
    setupHoverHandlers(viewer, isMobileDevice);
    setupGlobalEvents(viewer, isMobileDevice);

    // Setup BIM feature highlighting (opt-in)
    if (options.enableBimHoverHighlight) {
      setBimHighlightingEnabled(!!options.enableBimHoverHighlight);
    }

    // Calculate expected geoid undulation for the map location
    let mapLon = 13.8609; // Default fallback
    let mapLat = 50.9866; // Default fallback

    // Use mapData coordinates if available
    if (
      mapData?.bbox &&
      Array.isArray(mapData.bbox) &&
      mapData.bbox.length >= 2
    ) {
      mapLon = mapData.bbox[0];
      mapLat = mapData.bbox[1];
    }
    // Fallback to initial camera position
    else if (initialCameraPosition?.center) {
      mapLon = initialCameraPosition.center[0];
      mapLat = initialCameraPosition.center[1];
    }
    // Fallback to first asset metadata if available
    else if (assetMetadataMap && assetMetadataMap.size > 0) {
      const firstAsset = Array.from(assetMetadataMap.values())[0];
      if (firstAsset.lat && firstAsset.lng) {
        mapLon = firstAsset.lng;
        mapLat = firstAsset.lat;
      }
    }

    const calculatedUndulation = getSimpleGeoidUndulation(mapLon, mapLat);

    // Initialize geoid correction for BIM models (fixes underground model issues)
    initializeGeoidWithOffset(calculatedUndulation);

    // Drawing is now handled by the UI component, not in the core library
    if (options.polygonDrawingEnabled) {
    }

    // Set initial camera position if provided (no animation for initial load)
    if (initialCameraPosition) {
      const cameraOptions = createCameraOptions(initialCameraPosition, 0.0); // 0 duration for initial load
      viewer.camera.flyTo(cameraOptions);

      // Show the globe after camera is positioned
      viewer._initialFlightStarted = true;
    }

    // Load initial assets
    const tilesets = await loadMultipleTilesets(
      viewer,
      isMobileDevice,
      assetIds,
      bimAssetStyles,
      assetMetadataMap,
      false, // Initially load all assets without camera filtering
      cacheBytes,
      maximumCacheOverflowBytes,
    );

    // Apply initial styles after tilesets are loaded
    applyInitialStyles(viewer, tilesetRegistry, bimAssetStyles);

    // Set up BIM highlighting for existing tilesets if enabled
    if (options.enableBimHoverHighlight && tilesets) {
      tilesets.forEach((tileset) => {
        if (tileset) {
          setupBimHighlightingForExistingTileset(tileset);
        }
      });
    }

    // Only auto-zoom to first asset if no initial camera position or autoZoomToAssets is not false
    // Use 0 duration for initial load to avoid flying animation from globe
    if (
      (!hasInitialCameraPosition || autoZoomToAssets !== false) &&
      assetIds.length > 0
    ) {
      const firstTileset = _findAssetById(viewer, assetIds[0]);
      if (firstTileset) {
        // Set duration to 0 for initial load
        const boundingSphere = firstTileset.boundingSphere;
        if (boundingSphere) {
          viewer.camera.flyToBoundingSphere(boundingSphere, {
            duration: 0.0,
          });
        } else {
          viewer.zoomToTileset(firstTileset, { duration: 0.0 });
        }
      }
    }

    // Apply initial imagery style if provided
    if (imageryStyle && imageryStyle !== "mapbox-outdoors") {
      setTimeout(() => {
        changeImageryProvider(
          viewer,
          imageryStyle,
          isMobileDevice,
          clippingRectangle,
        ).catch((error) => {});
      }, 100); // Reduced from 1000ms to 100ms for faster loading
    }

    // Mark as ready
    setCameraReady(true);

    onReady?.(viewer);
  } catch (error: any) {
    console.error("Error initializing Cesium:", error);
  }
}

// Helper function to determine if performance mode should be enabled
function shouldEnablePerformanceMode(): boolean {
  return isMobileDevice;
}

export function flyToPosition(
  viewerInstance: any,
  flyToSettings: CesiumViewSettings,
  cesiumCameraPosition?: CesiumViewSettings,
) {
  _flyToPosition(flyToSettings, cesiumCameraPosition, viewerInstance);
}

// Public API functions
export function getViewer() {
  return viewer;
}

export function resetViewerState() {
  resetCameraState();
  // We don't reset the viewer object itself to avoid breaking React state
}

export function flyToView(
  viewerInstance: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  _flyToView(viewerInstance, settings, duration, onComplete);
}

export function updateCameraPosition(
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  return _updateCameraPosition(viewer, settings, duration, onComplete);
}

export function setCameraPosition(settings: CesiumViewSettings) {
  return _setCameraPosition(viewer, settings);
}

export function getCurrentCameraPosition(): CesiumViewSettings | null {
  return _getCurrentCameraPosition(viewer);
}

// Public API asset functions that use the internal viewer
export function loadAndZoomToAsset(
  assetId: number,
  duration?: number,
  containerId?: string,
) {
  // If containerId is provided, we should get the viewer for that specific container
  // For now, using the global viewer (this is a limitation of the current architecture)
  return _loadAndZoomToAsset(viewer, assetId, duration);
}

export function zoomToAssetById(
  assetId: number,
  duration?: number,
  containerId?: string,
) {
  // If containerId is provided, we should get the viewer for that specific container
  // For now, using the global viewer (this is a limitation of the current architecture)
  return _zoomToAssetById(viewer, assetId, duration);
}

export function removeAssetById(assetId: number, viewerInstance?: any) {
  // If a containerId is provided, it's ignored for now as we assume a single viewer.
  // This could be enhanced to support multiple viewers if needed.
  return _removeAssetById(viewerInstance || viewer, assetId);
}

export function findAssetById(assetId: number) {
  return _findAssetById(viewer, assetId);
}

// Enhanced tileset loading with smart filtering (BIM assets only)
export function loadSmartTilesets(
  bimAssetIds: number[] = [],
  styles?: StyleCondition[],
  assetMetadataMap?: Map<number, { lat: number; lng: number; radius?: number }>,
  enableCameraFiltering = true,
) {
  if (!viewer) return;

  const isMobile = isMobileDevice;

  // Use the updated loadMultipleTilesets function
  loadMultipleTilesets(
    viewer,
    isMobile,
    bimAssetIds,
    styles,
    assetMetadataMap,
    enableCameraFiltering,
  );
}

// Re-export types
export type {
  CameraOptions,
  CesiumViewSettings,
  InitializeCesiumOptions,
  StyleCondition,
} from "./cesium-types";

// DOM event handlers for settings
document.addEventListener("DOMContentLoaded", () => {
  const depthTestCheckbox = document.getElementById(
    "toggleDepthTest",
  ) as HTMLInputElement;
  const shadowsCheckbox = document.getElementById(
    "toggleShadows",
  ) as HTMLInputElement;

  function updateSettings() {
    if (viewer) {
      viewer.scene.globe.depthTestAgainstTerrain = depthTestCheckbox.checked;
      viewer.shadows = shadowsCheckbox.checked;
    }
  }

  if (depthTestCheckbox && shadowsCheckbox) {
    depthTestCheckbox.addEventListener("change", updateSettings);
    shadowsCheckbox.addEventListener("change", updateSettings);
  }
});

export {
  cleanupHoverHandlers,
  setBimHighlightingEnabled,
  setupBimHighlightingForExistingTileset,
  setupBimHoverHighlighting,
} from "./cesium-events";
// Consolidated exports
export {
  changeImageryProvider,
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "./cesium-imagery";
export { applyStylesToAllTilesets } from "./cesium-styling";

// Event handler for imagery style changes
function handleImageryStyleChange(event: any) {
  if (viewer && event.detail?.style) {
    const clippingRectangle = (window as any).cesiumClippingRectangle;
    changeImageryProvider(
      viewer,
      event.detail.style,
      isMobileDevice,
      clippingRectangle,
    ).catch((error) => {
      console.error("Error changing imagery provider:", error);
    });
  }
}
